/**
 * Service Registry
 * 
 * Centralized dependency injection configuration.
 * Registers all services, their dependencies, and manages
 * the application's service lifecycle.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import type { DIContainer } from './container';
import type { Config } from '../config/environment';
import type { 
  ICacheService,
  IAnalyticsService,
  IRealTimeService
} from '../services/interfaces/IDataService';
import type { ILogger } from '../services/logging/Logger';

import { LoggerFactory } from '../services/logging/Logger';
import { BlogPostService } from '../services/implementations/BlogPostService';
import { MemoryCacheService } from '../services/implementations/CacheService';
import { PerformanceService } from '../services/implementations/PerformanceService';
import { BookService } from '../services/implementations/BookService';
import { ProjectService } from '../services/implementations/ProjectService';
import { LegacyContentService } from '../services/implementations/LegacyContentService';
import { environmentService } from '../config/environment';

/**
 * Service identifiers for dependency injection
 */
export const SERVICE_IDENTIFIERS = {
  // Core services
  CONFIG: Symbol.for('Config'),
  LOGGER: Symbol.for('Logger'),
  
  // Cache services
  CACHE_SERVICE: Symbol.for('CacheService'),
  
  // Performance services
  PERFORMANCE_SERVICE: Symbol.for('PerformanceService'),
  
  // Data services
  BLOG_POST_SERVICE: Symbol.for('BlogPostService'),
  BOOK_SERVICE: Symbol.for('BookService'),
  PROJECT_SERVICE: Symbol.for('ProjectService'),
  LEGACY_CONTENT_SERVICE: Symbol.for('LegacyContentService'),
  
  // Feature services
  ANALYTICS_SERVICE: Symbol.for('AnalyticsService'),
  REAL_TIME_SERVICE: Symbol.for('RealTimeService'),
} as const;

/**
 * Service Registry Class
 * 
 * Configures and manages all application services
 */
export class ServiceRegistry {
  private isRegistered = false;

  /**
   * Register all services with the container
   */
  public registerServices(container: DIContainer): void {
    if (this.isRegistered) {
      throw new Error('Services are already registered');
    }

    this.registerCoreServices(container);
    this.registerDataServices(container);
    this.registerFeatureServices(container);

    this.isRegistered = true;
  }

  /**
   * Register core infrastructure services
   */
  private registerCoreServices(container: DIContainer): void {
    // Configuration service
    container.registerValue(SERVICE_IDENTIFIERS.CONFIG, environmentService.getAll());

    // Logging service
    container.registerSingleton(SERVICE_IDENTIFIERS.LOGGER, () => {
      const config = container.resolve<Config>(SERVICE_IDENTIFIERS.CONFIG);
      return LoggerFactory.create(config);
    });

    // Cache service
    container.registerSingleton(SERVICE_IDENTIFIERS.CACHE_SERVICE, () => {
      const logger = container.resolve<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
      const config = container.resolve<Config>(SERVICE_IDENTIFIERS.CONFIG);
      
      // For now, use memory cache. In production, this could be Redis
      return new MemoryCacheService(logger, config.CACHE_TTL);
    });

    // Performance service
    container.registerSingleton(SERVICE_IDENTIFIERS.PERFORMANCE_SERVICE, () => {
      return new PerformanceService();
    });
  }

  /**
   * Register data access services
   */
  private registerDataServices(container: DIContainer): void {
    // Blog post service
    container.registerSingleton(SERVICE_IDENTIFIERS.BLOG_POST_SERVICE, () => {
      const logger = container.resolve<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
      const cache = container.resolve<ICacheService>(SERVICE_IDENTIFIERS.CACHE_SERVICE);
      return new BlogPostService(logger, cache);
    });

    // Book service
    container.registerSingleton(SERVICE_IDENTIFIERS.BOOK_SERVICE, () => {
      const logger = container.resolve<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
      return new BookService(logger);
    });

    // Project service
    container.registerSingleton(SERVICE_IDENTIFIERS.PROJECT_SERVICE, () => {
      const logger = container.resolve<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
      return new ProjectService(logger);
    });

    // Legacy content service
    container.registerSingleton(SERVICE_IDENTIFIERS.LEGACY_CONTENT_SERVICE, () => {
      const logger = container.resolve<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
      return new LegacyContentService(logger);
    });
  }

  /**
   * Register feature services
   */
  private registerFeatureServices(container: DIContainer): void {
    // Analytics service
    container.registerSingleton(SERVICE_IDENTIFIERS.ANALYTICS_SERVICE, () => {
      const logger = container.resolve<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
      const config = container.resolve<Config>(SERVICE_IDENTIFIERS.CONFIG);
      
      if (config.ENABLE_ANALYTICS) {
        // TODO: Implement real analytics service
        return new AnalyticsServicePlaceholder(logger);
      } else {
        return new NoOpAnalyticsService();
      }
    });

    // Real-time service
    container.registerSingleton(SERVICE_IDENTIFIERS.REAL_TIME_SERVICE, () => {
      const logger = container.resolve<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
      const config = container.resolve<Config>(SERVICE_IDENTIFIERS.CONFIG);
      
      if (config.ENABLE_REAL_TIME && config.VITE_SUPABASE_URL) {
        // TODO: Implement real-time service
        return new RealTimeServicePlaceholder(logger);
      } else {
        return new NoOpRealTimeService();
      }
    });
  }

  /**
   * Validate service registration
   */
  public validateRegistration(container: DIContainer): void {
    const requiredServices = Object.values(SERVICE_IDENTIFIERS);
    
    for (const serviceId of requiredServices) {
      if (!container.has(serviceId)) {
        throw new Error(`Required service not registered: ${String(serviceId)}`);
      }
    }
  }

  /**
   * Get service dependencies for a given service
   */
  public getServiceDependencies(serviceId: symbol): symbol[] {
    const dependencies: Record<symbol, symbol[]> = {
      [SERVICE_IDENTIFIERS.CONFIG]: [],
      [SERVICE_IDENTIFIERS.LOGGER]: [SERVICE_IDENTIFIERS.CONFIG],
      [SERVICE_IDENTIFIERS.CACHE_SERVICE]: [SERVICE_IDENTIFIERS.LOGGER, SERVICE_IDENTIFIERS.CONFIG],
      [SERVICE_IDENTIFIERS.PERFORMANCE_SERVICE]: [],
      [SERVICE_IDENTIFIERS.BLOG_POST_SERVICE]: [SERVICE_IDENTIFIERS.LOGGER, SERVICE_IDENTIFIERS.CACHE_SERVICE],
      [SERVICE_IDENTIFIERS.BOOK_SERVICE]: [SERVICE_IDENTIFIERS.LOGGER],
      [SERVICE_IDENTIFIERS.PROJECT_SERVICE]: [SERVICE_IDENTIFIERS.LOGGER],
      [SERVICE_IDENTIFIERS.LEGACY_CONTENT_SERVICE]: [SERVICE_IDENTIFIERS.LOGGER, SERVICE_IDENTIFIERS.CACHE_SERVICE],
      [SERVICE_IDENTIFIERS.ANALYTICS_SERVICE]: [SERVICE_IDENTIFIERS.LOGGER, SERVICE_IDENTIFIERS.CONFIG],
      [SERVICE_IDENTIFIERS.REAL_TIME_SERVICE]: [SERVICE_IDENTIFIERS.LOGGER, SERVICE_IDENTIFIERS.CONFIG],
    };

    return dependencies[serviceId] || [];
  }
}

// Placeholder implementations (to be replaced with actual implementations)

/**
 * Placeholder service implementations
 */



class AnalyticsServicePlaceholder implements IAnalyticsService {
  private readonly logger: ILogger;

  constructor(logger: ILogger) {
    this.logger = logger;
    // Mark as intentionally unused until implementation
    void this.logger;
  }

  async trackPageView() { 
    return { success: true }; 
  }
  async trackInteraction() { 
    return { success: true }; 
  }
  async getAnalytics() { 
    return { success: false, error: 'AnalyticsService not implemented yet' }; 
  }
}

class NoOpAnalyticsService implements IAnalyticsService {
  async trackPageView() { 
    return { success: true }; 
  }
  async trackInteraction() { 
    return { success: true }; 
  }
  async getAnalytics() { 
    return { success: true, data: {} }; 
  }
}

class RealTimeServicePlaceholder implements IRealTimeService {
  private readonly logger: ILogger;

  constructor(logger: ILogger) {
    this.logger = logger;
    // Mark as intentionally unused until implementation
    void this.logger;
  }

  async subscribe() { 
    return { success: false, error: 'RealTimeService not implemented yet' }; 
  }
  async unsubscribe() { 
    return { success: true }; 
  }
  async broadcast() { 
    return { success: true }; 
  }
}

class NoOpRealTimeService implements IRealTimeService {
  async subscribe() { 
    return { success: true, data: () => {} }; 
  }
  async unsubscribe() { 
    return { success: true }; 
  }
  async broadcast() { 
    return { success: true }; 
  }
}

// Export singleton instance
export const serviceRegistry = new ServiceRegistry(); 